import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Activity } from '../../../../../lib/common/custom-activity/eventTypes';
import { trigger } from '../../../../../lib/common/postmonger/postmongerManager';

export interface DataSelectorState {
    selectedCdpObject: string | null;
    selectedCdpField: string | null;
    selectedSchemaEntry: string | null;

    selectedCdpObjectComparisonField: string | null;
    selectedComparisonType: string | null;
    comparisonText: string | null;

    dirty: boolean;
}

const initialState: DataSelectorState = {
    selectedCdpObject: null,
    selectedCdpField: null,
    selectedSchemaEntry: null,

    selectedCdpObjectComparisonField: null,
    selectedComparisonType: null,
    comparisonText: null,

    dirty: false,
};

export const dataSelectorSlice = createSlice({
    name: 'dataSelector',
    initialState,
    reducers: {
        updateStateFromActivity: (
            state: DataSelectorState,
            action: PayloadAction<Activity>,
        ) => {
            // Update the state to match the activity.
            const activity = action.payload;

            state.selectedCdpObject = getInArgumentValue(
                activity,
                'dataCloudObject',
            );
            state.selectedCdpField = getInArgumentValue(
                activity,
                'dataCloudObjectSelectField',
            );
            state.selectedSchemaEntry = getInArgumentValue(
                activity,
                'dataCloudObjectSelectFieldFilterValue',
            );
            state.selectedCdpObjectComparisonField = getInArgumentValue(
                activity,
                'dataCloudObjectComparisonField',
            );
            state.selectedComparisonType = getInArgumentValue(
                activity,
                'comparisonType',
            );
            state.comparisonText = getInArgumentValue(
                activity,
                'comparisonText',
            );

            state.dirty = false;
            trigger('setActivityDirtyState', false);
        },
        selectedCdpObjectChanged: (
            state: DataSelectorState,
            selection: PayloadAction<string>,
        ) => {
            if (state.selectedCdpObject !== selection.payload) {
                state.selectedCdpObject = selection.payload;
                state.selectedCdpField = null;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        selectedCdpFieldChanged: (
            state: DataSelectorState,
            selection: PayloadAction<string>,
        ) => {
            if (state.selectedCdpField !== selection.payload) {
                state.selectedCdpField = selection.payload;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        selectedSchemaEntryChanged: (
            state: DataSelectorState,
            selection: PayloadAction<string>,
        ) => {
            if (state.selectedSchemaEntry !== selection.payload) {
                state.selectedSchemaEntry = selection.payload;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        selectedCdpObjectComparisonFieldChanged: (
            state: DataSelectorState,
            selection: PayloadAction<string>,
        ) => {
            if (state.selectedCdpObjectComparisonField !== selection.payload) {
                state.selectedCdpObjectComparisonField = selection.payload;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        selectedComparisonTypeChanged: (
            state: DataSelectorState,
            selection: PayloadAction<string>,
        ) => {
            if (state.selectedComparisonType !== selection.payload) {
                state.selectedComparisonType = selection.payload;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        comparisonTextChanged: (
            state: DataSelectorState,
            selection: PayloadAction<string>,
        ) => {
            if (state.comparisonText !== selection.payload) {
                state.comparisonText = selection.payload;

                state.dirty = true;
                trigger('setActivityDirtyState', true);
            }
        },
        done: (state: DataSelectorState, action: PayloadAction<Activity>) => {
            if (!state.dirty) {
                console.warn('Nothing to save.');
                return;
            }

            if (
                state.selectedCdpObject === null ||
                state.selectedCdpField === null ||
                state.selectedSchemaEntry === null ||
                state.selectedCdpObjectComparisonField === null ||
                state.selectedComparisonType === null
            ) {
                console.error('Invalid selection.');
                return;
            }

            // Since the action is passed in from the component, rather than being
            // read from state directly, it is immutable. Duplicate it so we can add our
            // arguments and update Journey Builder.
            const activity = JSON.parse(JSON.stringify(action.payload));

            addOrUpdateInArgument(
                activity,
                'dataCloudObject',
                state.selectedCdpObject,
            );
            addOrUpdateInArgument(
                activity,
                'dataCloudObjectSelectField',
                state.selectedCdpField,
            );
            addOrUpdateInArgument(
                activity,
                'dataCloudObjectSelectFieldFilterValue',
                '{{' + state.selectedSchemaEntry + '}}',
            );
            addOrUpdateInArgument(
                activity,
                'dataCloudObjectComparisonField',
                state.selectedCdpObjectComparisonField,
            );
            addOrUpdateInArgument(
                activity,
                'comparisonType',
                state.selectedComparisonType,
            );

            if (state.comparisonText) {
                addOrUpdateInArgument(
                    activity,
                    'comparisonText',
                    state.comparisonText,
                );
            }

            activity['metaData'].isConfigured = true;

            trigger('updateActivity', activity);
            trigger('requestInspectorClose');
        },
        cancel: (state: DataSelectorState) => {
            // Tell Journey Builder that this activity has no changes.
            // we wont be prompted to save changes when the inspector closes
            trigger('setActivityDirtyState', false);

            // now request that Journey Builder closes the inspector/drawer
            trigger('requestInspectorClose');
        },
    },
});

export const {
    updateStateFromActivity,
    selectedCdpObjectChanged,
    selectedCdpFieldChanged,
    selectedSchemaEntryChanged,
    selectedCdpObjectComparisonFieldChanged,
    selectedComparisonTypeChanged,
    comparisonTextChanged,
    done,
    cancel,
} = dataSelectorSlice.actions;

function getInArgumentValue(
    activity: Activity,
    argument: string,
): string | null {
    if (activity?.arguments?.execute?.inArguments === undefined) {
        return null;
    }

    const inArguments = activity.arguments.execute.inArguments;

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if the argument doesn't exist
    if (executeArg === undefined || !(argument in executeArg)) {
        return null;
    }

    return executeArg[argument];
}

/**
 * Adds or updates an inArgument with the name `argument` and value `value`.
 * @param activity The activity to update
 * @param argument The argument name
 * @param value The argument value
 * @returns Whether anything was changed.
 */
function addOrUpdateInArgument(
    activity: Activity,
    argument: string,
    value: string,
): boolean {
    if (activity?.arguments?.execute?.inArguments === undefined) {
        return false;
    }

    const inArguments = activity.arguments.execute.inArguments;

    // See if we already have an argument with this name. We'll update the value if so.
    let executeArg = inArguments.find((item: any) => argument in item);

    // Bail if we don't need to change anything
    if (
        executeArg !== undefined &&
        argument in executeArg &&
        executeArg[argument] === value
    ) {
        return false;
    }

    if (executeArg === undefined) {
        executeArg = {};
        inArguments.push(executeArg);
    }

    executeArg[argument] = value;
    return true;
}
